<script>
import { GlDisclosureDropdownItem } from '@gitlab/ui';
import { computePosition, offset, flip, shift } from '@floating-ui/dom';
import { uniqueId } from 'lodash-es';
import { __ } from '~/locale';
import { createInputAdapter } from './input_adapters';

// A slash opens the menu at the start of the text or after whitespace. The
// leading boundary is what stops a pasted "https://example.com" triggering it.
const TRIGGER_RE = /(?:^|\s)(\/\S*)$/;

// Override via `inputSelector` when the slot holds more than one candidate.
const DEFAULT_INPUT_SELECTOR = 'textarea';

/**
 * Attaches a slash-command suggestion menu to a slotted text input, in the style
 * of vue-mention's Mentionable.
 *
 * The input is supplied by the caller through the default slot, so this
 * component never owns that vnode and cannot bind to it in the template. It
 * finds the node and attaches native listeners instead. Reading text and caret
 * position goes through an adapter (see input_adapters.js), so this component
 * holds no assumptions about the element type; supporting a contenteditable
 * later means adding an adapter, not changing anything here.
 *
 * Everything it observes is read-only: it never writes the input's content, and
 * applying a command is the parent's job via the `select` event.
 */
export default {
  name: 'SlashCommandsMenu',
  components: { GlDisclosureDropdownItem },
  props: {
    commands: {
      type: Array,
      required: true,
    },
    /**
     * The current prompt. Needed because the parent can change it through
     * v-model, which fires no native input event and would leave us stale.
     */
    value: {
      type: String,
      required: true,
    },
    /**
     * CSS selector locating the editable element inside the default slot.
     */
    inputSelector: {
      type: String,
      required: false,
      default: DEFAULT_INPUT_SELECTOR,
    },
  },
  emits: ['select'],
  data() {
    return {
      isOpen: false,
      activeIndex: 0,
      query: '',
      triggerIndex: 0,
      listboxId: uniqueId('slash-commands-listbox-'),
    };
  },
  computed: {
    filteredCommands() {
      const query = this.query.toLowerCase();
      // A `startOnly` command acts on the whole prompt, so offering it after
      // other text would produce something the parent cannot sensibly apply.
      const isPromptStart = this.triggerIndex === 0;

      return this.commands.filter(
        (command) =>
          command.name.toLowerCase().startsWith(query) && (isPromptStart || !command.startOnly),
      );
    },
    activeCommand() {
      return this.filteredCommands[this.activeIndex];
    },
  },
  watch: {
    // The commands can arrive after the user has typed the slash that needs them: the
    // keystroke found nothing to offer, and there is no later event to re-derive from.
    // Not conditional on the panel being open for that reason.
    commands() {
      this.evaluate();
    },
    value() {
      // The DOM has not patched yet, so the caret is still where it was.
      this.$nextTick(this.evaluate);
    },
    isOpen(isOpen) {
      this.syncAriaAttributes();
      if (isOpen) this.$nextTick(this.reposition);
    },
    activeIndex() {
      this.syncAriaAttributes();
      this.$nextTick(this.scrollActiveIntoView);
    },
  },
  mounted() {
    const inputEl = this.$el.querySelector(this.inputSelector);
    if (!inputEl) return;

    // Keystroke that should be consumed by the slash commands menu so the prompt composer doesn't react to it.
    this.swallowKeyup = null;

    this.adapter = createInputAdapter(inputEl);
    this.inputEl = inputEl;

    // Marks the input as the combobox that owns the suggestion list. Static
    // for the input's lifetime, unlike the state syncAriaAttributes tracks.
    this.inputEl.setAttribute('role', 'combobox');
    this.inputEl.setAttribute('aria-haspopup', 'listbox');

    // Watchers only fire on change, so without this the input carries no
    // aria-expanded until the menu first opens and assistive tech has no way to
    // know the field can expand a list at all.
    this.syncAriaAttributes();

    // Capture on the wrapper root, not the input: listeners on the event target
    // fire in registration order regardless of the capture flag, so an ancestor
    // is the only placement guaranteed to run before the composer's own
    // `.native` handlers.
    this.$el.addEventListener('keydown', this.onKeydown, true);
    this.$el.addEventListener('keyup', this.onKeyup, true);

    this.inputEl.addEventListener('input', this.evaluate);
    this.inputEl.addEventListener('keyup', this.evaluate);
    this.inputEl.addEventListener('click', this.evaluate);
    this.inputEl.addEventListener('scroll', this.onScroll);
    this.inputEl.addEventListener('focusout', this.onFocusout);
    this.inputEl.addEventListener('compositionstart', this.close);
  },
  beforeDestroy() {
    this.$el.removeEventListener('keydown', this.onKeydown, true);
    this.$el.removeEventListener('keyup', this.onKeyup, true);
    if (!this.inputEl) return;

    this.inputEl.removeEventListener('input', this.evaluate);
    this.inputEl.removeEventListener('keyup', this.evaluate);
    this.inputEl.removeEventListener('click', this.evaluate);
    this.inputEl.removeEventListener('scroll', this.onScroll);
    this.inputEl.removeEventListener('focusout', this.onFocusout);
    this.inputEl.removeEventListener('compositionstart', this.close);
  },
  methods: {
    optionId(index) {
      return `${this.listboxId}-option-${index}`;
    },
    evaluate() {
      if (!this.adapter) return;

      // A non-collapsed selection is not a caret, so there is nothing to anchor to.
      if (this.adapter.hasTextSelection()) {
        this.close();
        return;
      }

      const caret = this.adapter.getCaretOffset();
      const text = this.adapter.getText();

      // The caret has to sit at the end of the token, not somewhere before it.
      const after = text.slice(caret);
      if (after && !/^\s/.test(after)) {
        this.close();
        return;
      }

      const match = TRIGGER_RE.exec(text.slice(0, caret));
      if (!match) {
        this.close();
        return;
      }

      const [, token] = match;

      // Once the token *is* a command there is nothing left to suggest, and
      // leaving the menu open would make Enter re-select instead of send.
      // Compared by equality, not prefix, so /resetall stays completable.
      if (this.commands.some((command) => command.name.toLowerCase() === token.toLowerCase())) {
        this.close();
        return;
      }

      this.query = token;
      this.triggerIndex = caret - token.length;

      if (!this.filteredCommands.length) {
        this.close();
        return;
      }

      if (!this.isOpen) {
        this.activeIndex = 0;
        this.isOpen = true;
      } else {
        this.activeIndex = Math.min(this.activeIndex, this.filteredCommands.length - 1);
        this.$nextTick(this.reposition);
      }
    },
    close() {
      if (this.isOpen) this.isOpen = false;
    },
    onFocusout(event) {
      if (!this.$el.contains(event.relatedTarget)) this.close();
    },
    onScroll() {
      if (this.isOpen) this.reposition();
    },
    async reposition() {
      const { panel } = this.$refs;
      if (!this.adapter || !panel) return;

      const { top, left, height } = this.adapter.getCaretRect(this.triggerIndex);

      // A virtual element: floating-ui only needs a rect, so there is no need
      // for a real anchor node sitting in the DOM at the caret.
      const reference = { getBoundingClientRect: () => new DOMRect(left, top, 1, height) };

      const { x, y } = await computePosition(reference, panel, {
        strategy: 'fixed',
        placement: 'top-start',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
      });

      Object.assign(panel.style, { left: `${x}px`, top: `${y}px` });
    },
    onKeydown(event) {
      if (!this.isOpen || event.target !== this.inputEl) return;
      // keyCode 229 is what browsers report while an IME is composing.
      if (event.isComposing || event.keyCode === 229) return;
      if (event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return;

      const handler = {
        ArrowDown: () => this.moveActive(1),
        ArrowUp: () => this.moveActive(-1),
        Enter: () => this.selectActive(),
        Tab: () => this.selectActive(),
        Escape: () => this.close(),
      }[event.key];

      if (!handler) return;

      event.preventDefault();
      event.stopPropagation();
      // The composer sends the message on Enter *keyup*, so consuming only the
      // keydown would still let the release through and submit. Mark the key so
      // its release is swallowed too.
      this.swallowKeyup = event.key;
      handler();
    },
    onKeyup(event) {
      // One-shot: any keyup clears the mark, so a stale one cannot swallow a
      // later, unrelated press.
      const swallowed = this.swallowKeyup;
      this.swallowKeyup = null;

      if (swallowed !== event.key) return;

      event.preventDefault();
      event.stopPropagation();
    },
    moveActive(delta) {
      const { length } = this.filteredCommands;
      this.activeIndex = (this.activeIndex + delta + length) % length;
    },
    selectActive() {
      if (this.activeCommand) this.emitSelect(this.activeCommand);
    },
    emitSelect(command) {
      // The matched range travels with the event so the parent can splice the
      // token rather than replace the prompt, which would discard anything the
      // user had already written around it.
      this.$emit('select', command, { triggerIndex: this.triggerIndex, token: this.query });
      this.close();
    },
    scrollActiveIntoView() {
      document.getElementById(this.optionId(this.activeIndex))?.scrollIntoView({
        block: 'nearest',
      });
    },
    syncAriaAttributes() {
      if (!this.inputEl) return;

      if (!this.isOpen) {
        this.inputEl.removeAttribute('aria-activedescendant');
        this.inputEl.removeAttribute('aria-controls');
        this.inputEl.setAttribute('aria-expanded', 'false');
        return;
      }

      this.inputEl.setAttribute('aria-expanded', 'true');
      this.inputEl.setAttribute('aria-controls', this.listboxId);
      this.inputEl.setAttribute('aria-activedescendant', this.optionId(this.activeIndex));
    },
    // Keeps focus in the input for the whole click, so focusout never fires
    // and the follow-up click still reaches the option.
    onPanelMousedown(event) {
      event.preventDefault();
    },
  },
  i18n: {
    LISTBOX_LABEL: __('Slash commands'),
  },
};
</script>

<template>
  <div>
    <slot></slot>
    <div data-testid="slash-commands-panel-container" class="gl-new-dropdown-container gl-relative">
      <div
        v-if="isOpen"
        ref="panel"
        class="gl-new-dropdown-panel gl-fixed gl-left-0 gl-top-0 !gl-block gl-w-33"
        data-testid="slash-commands-panel"
        @mousedown="onPanelMousedown"
      >
        <ul
          :id="listboxId"
          role="listbox"
          :aria-label="$options.i18n.LISTBOX_LABEL"
          class="gl-new-dropdown-inner gl-mb-0 gl-list-none gl-overflow-auto gl-py-1 gl-pl-0"
        >
          <!-- role="option" is not decorative: aria-activedescendant only
             announces the highlighted row if it is an option in a listbox.
             aria-selected is deliberately absent, since choosing a command runs
             it rather than selecting anything. -->
          <gl-disclosure-dropdown-item
            v-for="(command, index) in filteredCommands"
            :id="optionId(index)"
            :key="command.name"
            role="option"
            tabindex="-1"
            :class="{ 'gl-new-dropdown-item-highlighted': index === activeIndex }"
            @action="emitSelect(command)"
          >
            <!-- The default slot rather than #list-item: that one renders the
               content inside a <button>, and ARIA forbids an interactive
               element inside role="option". These are the classes the library
               would have supplied, kept so hover and highlight still apply. -->
            <span class="gl-new-dropdown-item-content">
              <span class="gl-new-dropdown-item-text-wrapper">
                <span>{{ command.name }}</span>
                <p class="gl-mb-0 gl-text-sm gl-text-subtle">{{ command.description }}</p>
              </span>
            </span>
          </gl-disclosure-dropdown-item>
        </ul>
      </div>
    </div>
  </div>
</template>
